import React, {
  useEffect,
  useContext,
  useState,
  useLayoutEffect,
  useRef,
} from "react";
import AppImages from "../../../../../../../constants/images";
import PdfURLContext from "../../../../../../../context/pdfURL/pdfURLContext";
import { toast } from "react-toastify";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import rehypeRaw from "rehype-raw";
import $ from "jquery";
import { getMultiFileClassByExtension } from "../../../../shared/fileExtension";
import UserImage from "../../../../../../common/userImage/index.js";
import { UserContext } from "../../../../../../../context/user/userContext.js";
import MarkDownImages from "../markDownImages/index.js";
import { checkIfPageNoExists } from "../../../../../../../services/commonService";
import { isPDF } from "../../../../../../../services/commonService";
import { useConfig } from "../../../../../../../context/config/configContext";

const useTypingEffect = (
  text,
  speed = 100, // Speed between word chunks
  isActive = true,
  replyFromBot,
  isAllowedMessageStreaming
) => {
  const [displayedText, setDisplayedText] = useState("");
  const [isTypingComplete, setIsTypingComplete] = useState(false);
  const [isReplyFromBot, setIsReplyFromBot] = useState(false);
  const [isTypingInProgress, setIsTypingInProgress] = useState(false);
  const textRef = useRef(text);
  const isActiveRef = useRef(isActive);

  useEffect(() => {
    textRef.current = text;
    isActiveRef.current = isActive;
  }, [text, isActive]);

  useEffect(() => {
    if (!isActive || !text) {
      setDisplayedText(text || "");
      setIsTypingComplete(true);
      return;
    }

    if (replyFromBot && isAllowedMessageStreaming) {
      setDisplayedText("");
      setIsTypingComplete(false);
      setIsTypingInProgress(true);

      // Split into words but keep spaces
      const words = text.match(/\S+\s*/g) || [];
      let wordIndex = 0;

      // Show first batch of words immediately
      const initialBatch = Math.min(5, words.length);
      let initialText = "";
      for (let i = 0; i < initialBatch; i++) {
        initialText += words[i];
      }
      setDisplayedText(initialText);
      wordIndex = initialBatch;

      const typeNextBatch = () => {
        if (wordIndex < words.length) {
          // Determine batch size (more words for longer text)
          let batchSize = 1;
          if (words.length > 100) batchSize = 3;
          if (words.length > 300) batchSize = 5;
          if (words.length > 500) batchSize = 8;

          // Add next batch of words
          let nextText = "";
          const endIndex = Math.min(wordIndex + batchSize, words.length);
          for (let i = wordIndex; i < endIndex; i++) {
            nextText += words[i];
          }

          setDisplayedText((prev) => prev + nextText);
          wordIndex = endIndex;

          // Continue typing
          setTimeout(typeNextBatch, speed);
        } else {
          setIsReplyFromBot(false);
          setIsTypingComplete(true);
          setIsTypingInProgress(false);
        }
      };

      // Start typing the rest
      setTimeout(typeNextBatch, speed);
    } else {
      setDisplayedText(text);
      setIsTypingComplete(true);
    }
  }, [text, isActive, speed, replyFromBot, isAllowedMessageStreaming]);

  return {
    displayedText,
    isTypingComplete,
    isReplyFromBot,
    isTypingInProgress,
  };
};

const TextChatbot = ({
  showChatLoader,
  messages,
  initials,
  displayLastCitations,
  lastDisplayMessage,
  setOpenPdfViewer,
  setSelectedCitation,
  isTyping,
  setIsMKSProcessing,
  setIsSkillProcessing,
  pdfClass,
  setPdfClass,
  showFollowupQuestion,
  disableFolloupButton,
  handleSendMessage,
  lastImageArray,
  isHybridChat,
  onLinkClick,
  skill,
  IsLinkClicked,
  setIsReplyFromBot,
  isReplyFromBot,
  setIsMessageTyping,
  isMessageTyping,
  isAllowedMessageStreaming,
  setTypingMessage,
}) => {
  const { pageNo, setPageNo, url, setUrl, showTab, setShowTab } =
    useContext(PdfURLContext);
  const { state } = useContext(UserContext);
  const { config } = useConfig();
  const imagebytes = state?.user?.userProfile?.userImage;
  const imagetype = state?.user?.userProfile?.userImagemimeType;
  const [isTextLoading, setIsTextLoading] = useState(false);
  const [lastMessageForTyping, setLastMessageForTyping] = useState("");

  const isLastMessage =
    messages.length > 0 &&
    messages[messages.length - 1].type === "reply" &&
    !isTextLoading;

  const {
    displayedText: typedOutput,
    isTypingComplete,
    isTypingInProgress,
  } = useTypingEffect(
    isLastMessage ? lastDisplayMessage : "",
    100,
    isLastMessage,
    isReplyFromBot,
    isAllowedMessageStreaming
  );

  useEffect(() => {
    setTypingMessage(typedOutput);
  }, [typedOutput]);

  useEffect(() => {
    const timeout = setTimeout(() => {
      setIsMessageTyping(isTypingInProgress);
    }, 2000);

    return () => clearTimeout(timeout);
  }, [isTypingInProgress]);

  useLayoutEffect(() => {
    if (lastDisplayMessage?.length > 0) {
      setIsTextLoading(false);
      setLastMessageForTyping(lastDisplayMessage);
    } else {
      setIsTextLoading(true);
    }
  }, [lastImageArray, lastDisplayMessage?.length]);

  useEffect(() => {
    if (isLastMessage && lastDisplayMessage) {
      setLastMessageForTyping(lastDisplayMessage);
    }
  }, [lastDisplayMessage, messages.length]);

  const ReplaceDocsComponentAll = (text, citations, arrayList, msg, index) => {
    const replacedText = replaceDocs(text, citations, arrayList, false, index);
    return <MarkDownImages msg={msg} replacedText={replacedText} />;
  };

  const displayAllMessage = (msg, index) => {
    let arrayList = [];
    return (
      <>
        {ReplaceDocsComponentAll(
          msg.text,
          msg.citations,
          arrayList,
          msg,
          index
        )}
        {arrayList
          .sort((a, b) => a - b)
          .map((index) => {
            const item = msg.citations[parseInt(index) - 1];
            if (item !== undefined) {
              return (
                <li key={index}>
                  <span
                    className="linkclass"
                    onClick={() => handleOpenPdfViewer(item)}
                  >
                    {index} - {item.filepath}
                  </span>
                </li>
              );
            }
          })}
      </>
    );
  };

  const handleOpenPdfViewer = (item) => {
    let newPageNumber = null;
    if (checkIfPageNoExists(item.filepath)) {
      const match = item.filepath.match(/#page=(\d+)/);
      newPageNumber = parseInt(match[1], 10);
    }

    if (
      url !== item.url ||
      url === "" ||
      !IsLinkClicked ||
      pageNo !== newPageNumber
    ) {
      onLinkClick(true);
      setUrl("");
      setPageNo(0);
      setPdfClass(false);
    } else {
      onLinkClick(false);
      setUrl("");
      setPageNo(0);
      setPdfClass(true);
    }

    const MKS_APP_VIEW_DOCUMENT_API_PARAMS = skill?.ChatbotConfiguration
      ?.CitationParams
      ? skill.ChatbotConfiguration.CitationParams
      : config.REACT_APP_VIEW_DOCUMENT_API_PARAMS || "";

    let baseUrl = skill?.ChatbotConfiguration?.CitationURL
      ? skill.ChatbotConfiguration.CitationURL
      : config.REACT_APP_VIEW_DOCUMENT_API_URL || "";

    if (baseUrl && item.url.indexOf(baseUrl) === -1) {
      item.url = baseUrl + item.url;
    }
    if (MKS_APP_VIEW_DOCUMENT_API_PARAMS && item.url.indexOf(MKS_APP_VIEW_DOCUMENT_API_PARAMS) === -1) {
      item.url = item.url + MKS_APP_VIEW_DOCUMENT_API_PARAMS;
    }

    setOpenPdfViewer(true);
    setSelectedCitation(item);

    if (checkIfPageNoExists(item.filepath)) {
      const match = item.filepath.match(/#page=(\d+)/);
      setPageNo(parseInt(match[1], 10));
    } else {
      setPageNo(1);
    }
    setUrl(item.url);
    if (!isPDF(item.url)) {
      setShowTab(1);
    }
  };

  const displayLastMessage = () => {
    let arrayList = [];

    const textToDisplay =
      !isTypingComplete && typedOutput ? typedOutput : lastDisplayMessage;

    return (
      <>
        {displayLastCitations.length === 0 &&
          ReplaceDocsComponent(textToDisplay, [], arrayList, true)}
        {displayLastCitations.length > 0 &&
          ReplaceDocsComponent(
            textToDisplay,
            displayLastCitations,
            arrayList,
            true
          )}

        {arrayList
          .sort((a, b) => a - b)
          .map((index) => {
            const item = displayLastCitations[parseInt(index) - 1];
            if (item !== undefined) {
              return (
                <li key={index}>
                  <span
                    title={item?.title ? decodeURIComponent(item?.title) : ""}
                    className="linkclass"
                    onClick={() => handleOpenPdfViewer(item)}
                  >
                    {index} - {item.filepath}
                  </span>
                </li>
              );
            }
          })}
      </>
    );
  };

  const ReplaceDocsComponent = (text, citations, arrayList, isLastMessage) => {
    if (text === undefined || text === null || text === "") return null;

    const replacedText = replaceDocs(text, citations, arrayList, isLastMessage);
    return (
      <ReactMarkdown
        remarkPlugins={[remarkGfm]}
        components={{
          ol: ({ children }) => (
            <ol style={{ paddingLeft: "1.5rem", margin: "0" }}>{children}</ol>
          ),
          li: ({ children }) => (
            <li style={{ whiteSpace: "normal", wordBreak: "break-word" }}>
              {children}
            </li>
          ),
          img: ImageRenderer,
        }}
        rehypePlugins={[rehypeRaw]}
      >
        {replacedText}
      </ReactMarkdown>
    );
  };

  useEffect(() => {
    $(document).on("click", ".linkclassText", function () {
      const dataIndex = $(this).data("index");
      const index = $(this).text() - 1;
      let citation = [];
      if (dataIndex === "undefined") {
        citation = displayLastCitations?.flatMap((msg) => msg || [])[index];
      } else {
        citation = messages[dataIndex].citations[index];
      }

      if (citation) {
        handleOpenPdfViewer(citation);
      }
    });

    return () => {
      $(document).off("click", ".linkclassText");
    };
  }, [messages, config]);

  const replaceDocs = (text, citations, arrayList, isLastMessage, index) => {
    if (!text) return "";

    const docPattern = /\[doc(\d+)\]/g;
    return text.replace(docPattern, (match, p1) => {
      let findCitation = citations[parseInt(p1) - 1];
      if (!arrayList?.includes(p1)) {
        arrayList?.push(p1);
      }

      if (findCitation) {
        return `<span data-index="${index}" className="linkclassText linkbox">${p1}</span>`;
      }
      return match; // Return original if citation not found
    });
  };

  const ImageRenderer = ({ src, alt, title }) => {
    if (
      lastImageArray !== null &&
      lastImageArray !== undefined &&
      lastImageArray?.length > 0
    ) {
      const imagesrc =
        lastImageArray.find((img) => img.name === alt)?.image || src;
      if (imagesrc !== "" && imagesrc.length > 1000) {
        return (
          <img
            src={imagesrc}
            alt={alt}
            title={title}
            className="chat-img-box"
          />
        );
      }
    }
    return null;
  };

  const getCopyButton = (msgText) => {
    if (!msgText) return null;

    return (
      <button
        className="copy-clipboard"
        aria-label="copy to clipboard"
        data-title="Copy"
        onClick={() => {
          const textToCopy = msgText;
          if (navigator.clipboard && navigator.clipboard.writeText) {
            navigator.clipboard
              .writeText(textToCopy)
              .then(() => {
                toast.success("Copied to clipboard successfully");
              })
              .catch((err) => {
                console.error("Clipboard API failed:", err);
                fallbackCopyText(textToCopy);
              });
          } else {
            fallbackCopyText(textToCopy);
          }
        }}
      ></button>
    );
  };

  const enableTab = () => {
    setIsMKSProcessing(false);
    setIsSkillProcessing(false);
  };

  const disableTab = (msg) => {
    setIsMKSProcessing(false);
    setIsSkillProcessing(
      msg?.text === "loader" && msg?.type === "reply" ? true : false
    );
  };

  const fallbackCopyText = (text) => {
    const textArea = document.createElement("textarea");
    textArea.value = text;

    textArea.style.position = "fixed";
    textArea.style.opacity = "0";
    document.body.appendChild(textArea);

    textArea.focus();
    textArea.select();

    try {
      const successful = document.execCommand("copy");
      if (successful) {
        toast.success("Copied to clipboard successfully");
      } else {
        toast.error("Failed to copy");
      }
    } catch (err) {
      console.error(" copy failed:", err);
      toast.error(" failed to copy!");
    }

    document.body.removeChild(textArea);
  };

  const setSelectedQuestion = (question) => {
    handleSendMessage(question);
  };

  const handleDownloadFile = (file) => {
    const url = URL.createObjectURL(file);
    const link = document.createElement("a");
    link.href = url;
    link.download = file.name;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  };

  const getFollowupQuestions = (msg) => {
    return (
      <>
        {msg?.followupQuestion?.length > 0 && showFollowupQuestion ? (
          <div className="follow-up-section">
            <div className="chat-line">Follow up Questions :</div>
            <div className="suggest-line-wrapper">
              {msg?.followupQuestion?.map((quest, index) => (
                <div
                  key={index}
                  className={
                    isTextLoading
                      ? "disabled-element suggest-line-disabled"
                      : "suggest-line"
                  }
                >
                  <div
                    className={isTextLoading ? "suggest-line-disabled-div" : ""}
                    onClick={() => setSelectedQuestion(quest)}
                  >
                    {quest}
                  </div>
                </div>
              ))}
            </div>
          </div>
        ) : (
          ""
        )}
      </>
    );
  };

  return (
    <>
      {messages.map((msg, index) =>
        msg.type === "user" ? (
          <div className="chat-msg user" key={index + " _" + 2}>
            <div className="user-profile-menu w-inline-block">
              <div className="user-profile-initial  auto-cursor">
                <UserImage
                  userImageBytes={imagebytes}
                  userImageMimeType={imagetype}
                  userInitials={initials}
                ></UserImage>
              </div>
            </div>

            <div className="chat-icon-text">
              <div className="msg-text">
                <div
                  className="table-chat-view"
                  style={{ whiteSpace: "pre-line" }}
                >
                  {msg.text}
                </div>
                <div className="multifile-row">
                  {msg?.files?.map((file) => {
                    const extensionClassName = getMultiFileClassByExtension(
                      file.name
                    );
                    return (
                      <div key={file.name} className="multifile">
                        <div className="upload-drag">
                          <div
                            title="Click to download"
                            onClick={() => handleDownloadFile(file)}
                            className={`${extensionClassName}-icon-list chat-file-dowbload`}
                          >
                            {file.name}
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            </div>

            {msg.isErrorOccured === true && (
              <div className="not-sent-icon">!</div>
            )}
          </div>
        ) : (
          <div className="chat-msg reply" key={index + " _" + 1}>
            <div className="chat-user-icon">
              <img
                src={msg.imageURL}
                loading="lazy"
                alt=""
                className="chat-user-img"
              />
            </div>
            <div className="chat-icon-text">
              <div className="msg-text">
                <div className="chat-text">
                  {messages.length - 1 === index && isTextLoading && (
                    <img src={AppImages.chatLoader} alt="" />
                  )}
                  <div
                    className="table-chat-view"
                    style={{ whiteSpace: "pre-line" }}
                  >
                    {messages.length - 1 === index
                      ? displayLastMessage()
                      : msg.text === "loader"
                      ? ""
                      : displayAllMessage(msg, index)}

                    {messages.length - 1 === index
                      ? ""
                      : isHybridChat && msg?.files?.length > 0
                      ? ""
                      : !isMessageTyping
                      ? getCopyButton(msg.text)
                      : ""}

                    {messages.length - 1 === index
                      ? ""
                      : getFollowupQuestions(msg)}

                    {!isTextLoading &&
                      !isMessageTyping &&
                      (msg?.files?.length === 0 ||
                        msg?.files === undefined ||
                        msg?.files === null) &&
                      getCopyButton(msg.text)}
                    {!isTextLoading && enableTab()}
                    {isTextLoading && disableTab(msg)}
                  </div>
                </div>

                {messages.length - 1 === index &&
                  !isTextLoading &&
                  !isMessageTyping &&
                  isTypingComplete &&
                  getFollowupQuestions(msg)}
              </div>
            </div>
          </div>
        )
      )}
    </>
  );
};

export default TextChatbot;
